import React, { useState } from "react";
import { Phone, Mail, Globe, MapPin, Linkedin, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/site/Reveal";
import { submitContact } from "../lib/api";
import { CONTACT, SERVICE_OPTIONS } from "../data/content";

const EMPTY = { full_name: "", organisation: "", email: "", telephone: "", service_required: "", message: "", preferred_date: "" };

const Field = ({ label, name, type = "text", value, onChange, required, ...rest }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-off-white/60">{label}{required && " *"}</span>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required}
      data-testid={`contact-${name}`}
      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-off-white placeholder-off-white/30 outline-none transition-colors duration-300 focus:border-gold"
      {...rest}
    />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you — your enquiry has been received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden emerald-radial py-24 text-off-white sm:py-32" data-testid="contact-section">
      <div className="grain absolute inset-0" />
      <div className="relative z-10 mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <Reveal><Overline dark>Contact</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight sm:text-5xl">
              Let's start the <span className="text-gradient-gold">conversation</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8">
              <p className="font-serif text-2xl">{CONTACT.name}</p>
              <p className="text-sm text-gold-light">{CONTACT.role}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-5 text-sm">
              {CONTACT.phones.map((p) => (
                <li key={p} className="flex items-center gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-base/50 text-emerald-light"><Phone size={17} /></span><a href={`tel:${p.replace(/\s/g, "")}`} data-testid={`contact-phone-${p.replace(/[^0-9]/g, "")}`} className="transition-colors hover:text-gold">{p}</a></li>
              ))}
              <li className="flex items-center gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-base/50 text-emerald-light"><Mail size={17} /></span>{CONTACT.email}</li>
              <li className="flex items-center gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-base/50 text-emerald-light"><Globe size={17} /></span>{CONTACT.website}</li>
              <li className="flex items-center gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-base/50 text-emerald-light"><MapPin size={17} /></span>{CONTACT.location}</li>
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`https://wa.me/${CONTACT.phoneRaw.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
                className="flex items-center gap-2 rounded-full bg-emerald-light px-5 py-3 text-sm font-semibold text-emerald-darkest transition-transform duration-300 hover:-translate-y-0.5">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-btn"
                className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-off-white transition-colors duration-300 hover:border-gold hover:text-gold">
                <Mail size={16} /> Email
              </a>
              <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin"
                className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-off-white transition-colors duration-300 hover:border-gold hover:text-gold">
                <Linkedin size={16} /> LinkedIn
              </a>
            </div>
            <p className="mt-6 inline-block rounded-full border border-gold/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold-light">
              Service Coverage: {CONTACT.coverage}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="glass rounded-[1.75rem] p-6 sm:p-9">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required placeholder="Jane Doe" />
              <Field label="Organisation" name="organisation" value={form.organisation} onChange={onChange} placeholder="Company Ltd" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="jane@company.com" />
              <Field label="Telephone" name="telephone" value={form.telephone} onChange={onChange} placeholder="+44 7000 000000" />
              <label className="block sm:col-span-1">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-off-white/60">Service Required</span>
                <select name="service_required" value={form.service_required} onChange={onChange} data-testid="contact-service_required"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-off-white outline-none transition-colors duration-300 focus:border-gold">
                  <option value="" className="bg-emerald-darkest">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-emerald-darkest">{s}</option>)}
                </select>
              </label>
              <Field label="Preferred Consultation Date" name="preferred_date" type="date" value={form.preferred_date} onChange={onChange} />
            </div>
            <label className="mt-5 block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-off-white/60">Message *</span>
              <textarea name="message" value={form.message} onChange={onChange} required rows={4} data-testid="contact-message"
                placeholder="Tell us about your goals or challenges…"
                className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-off-white placeholder-off-white/30 outline-none transition-colors duration-300 focus:border-gold" />
            </label>
            <button type="submit" disabled={loading} data-testid="contact-submit-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-emerald-darkest transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Sending…" : "Submit Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
